import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SceneHead, routesFor, type Lang } from "../primitives";

const t = {
  es: {
    aria: "Consultoría",
    label: "Consultoría",
    title: "Luis ROC: criterio estratégico y participación directa.",
    body:
      "Participo directamente en el diagnóstico, en la definición de prioridades y en la lectura de resultados. Según el alcance del proyecto, la ejecución puede realizarla tu propio equipo, un equipo colaborador o yo mismo; lo que se define al contratar es qué parte corresponde a cada uno.",
    note:
      "No implica disponibilidad ilimitada. Las áreas atendidas de forma remota no equivalen a oficinas: la sede está en el área de Houston, Texas.",
    facts: [
      "Más de 25 años de experiencia en marketing y búsqueda.",
      "Fundador de ROC Worldwide Agency LLC.",
      "Trabajo en español e inglés.",
      "Fabricantes y empresas de servicios.",
      "Houston como mercado principal y atención remota en Estados Unidos.",
    ],
    cta: "Sobre Luis ROC",
    alt: "Luis ROC presentando datos de comportamiento digital ante una audiencia profesional.",
    caption: "Fotografía real de una ponencia profesional, no una imagen de archivo.",
  },
  en: {
    aria: "Consulting",
    label: "Consulting",
    title: "Luis ROC: strategic judgment and direct involvement.",
    body:
      "I take part directly in the diagnosis, in setting priorities and in reading results. Depending on the scope, execution can be handled by your own team, a partner team or by me; what each side owns is defined when the engagement starts.",
    note:
      "This does not imply unlimited availability. Areas served remotely are not offices: the base is in the Houston, Texas area.",
    facts: [
      "More than 25 years of experience in marketing and search.",
      "Founder of ROC Worldwide Agency LLC.",
      "Work in Spanish and English.",
      "Manufacturers and service companies.",
      "Houston as the main market, with remote work across the United States.",
    ],
    cta: "About Luis ROC",
    alt: "Luis ROC presenting digital behaviour data to a professional audience.",
    caption: "Real photograph from a professional talk, not a stock image.",
  },
} as const;

export const SceneConsultant = ({ language = "es" as Lang }: { language?: Lang }) => {
  const c = t[language];
  const routes = routesFor(language);

  return (
    <section className="v3-section bg-white" aria-label={c.aria}>
      <div className="v3-shell grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-6">
          <SceneHead index="06" label={c.label} title={c.title} />
          <p className="v3-body mt-7">{c.body}</p>
          <p className="v3-note mt-4">{c.note}</p>

          <ul className="mt-8 border-t border-[color:var(--v3-line)]">
            {c.facts.map((f) => (
              <li
                key={f}
                className="border-b border-[color:var(--v3-line)] py-3.5 text-[0.97rem] text-[color:var(--v3-ink-soft)]"
              >
                {f}
              </li>
            ))}
          </ul>

          <Link to={routes.about} className="v3-inline-link mt-8">
            {c.cta}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        <Reveal className="lg:col-span-6" delay={0.08}>
          <figure>
            <img
              src="/media/luis-roc/luis-roc-seminar-1.png"
              alt={c.alt}
              className="w-full bg-[color:var(--v3-mineral)] object-cover"
              width={1200}
              height={900}
              loading="lazy"
              decoding="async"
            />
            <figcaption className="v3-caption mt-3">{c.caption}</figcaption>
          </figure>
        </Reveal>
      </div>
    </section>
  );
};
